import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { protectedResources } from 'src/app/auth/auth.config';
import { ObservableTtlCache } from 'src/app/utils/observable-ttl-cache';
import {
  CompanyMemberType,
  CompanyNameType,
  CompanyType,
  InviteToCompanyType,
} from '../models/company.model';

type CompanyListResponse =
  | CompanyNameType[]
  | {
      companies?: CompanyNameType[];
      list?: CompanyNameType[];
      items?: CompanyNameType[];
    };

@Injectable({
  providedIn: 'root',
})
export class MasterAdminCompanyService {
  private readonly apiEndpoint: string =
    protectedResources.apiTodoList.endpoint;
  private readonly companyCacheTtlMs = 60_000;
  private readonly allCompaniesCache = new ObservableTtlCache<
    CompanyNameType[]
  >(this.companyCacheTtlMs);

  constructor(private readonly http: HttpClient) {}

  getAllCompanies(): Observable<CompanyNameType[]> {
    return this.allCompaniesCache.get('all', () =>
      this.http
        .get<CompanyListResponse>(`${this.apiEndpoint}/companies`)
        .pipe(
          map((response) =>
            this.sortCompanies(this.extractCompanies(response)),
          ),
        ),
    );
  }

  getCompany(companyId: string): Observable<CompanyType> {
    return this.http.get<CompanyType>(
      `${this.apiEndpoint}/companies/${companyId}`,
    );
  }

  createCompany(payload: CompanyType): Observable<CompanyType> {
    return this.http
      .post<CompanyType>(`${this.apiEndpoint}/companies`, payload)
      .pipe(tap(() => this.clearCompaniesCache()));
  }

  updateCompany(
    companyId: string,
    payload: CompanyType,
  ): Observable<CompanyType> {
    return this.http
      .put<CompanyType>(`${this.apiEndpoint}/companies/${companyId}`, payload)
      .pipe(tap(() => this.clearCompaniesCache()));
  }

  deleteCompany(companyId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.apiEndpoint}/companies/${companyId}`)
      .pipe(tap(() => this.clearCompaniesCache()));
  }

  getCompanyMembers(companyId: string): Observable<CompanyMemberType[]> {
    return this.http.get<CompanyMemberType[]>(
      `${this.apiEndpoint}/companies/${companyId}/members`,
    );
  }

  assignCompanyRole(
    companyId: string,
    userId: string,
    payload: Partial<Pick<CompanyMemberType, 'role' | 'companyRole'>>,
  ): Observable<CompanyMemberType> {
    return this.http.put<CompanyMemberType>(
      `${this.apiEndpoint}/companies/${companyId}/roles/${userId}`,
      payload,
    );
  }

  getInvitationLink(companyId: string): Observable<string> {
    return this.http.get(`${this.apiEndpoint}/companies/${companyId}/invite`, {
      responseType: 'text',
    });
  }

  inviteUser(
    companyId: string,
    invite: InviteToCompanyType | string,
  ): Observable<string> {
    const payload: InviteToCompanyType =
      typeof invite === 'string' ? { email: invite } : invite;

    return this.http.post(
      `${this.apiEndpoint}/companies/${companyId}/invite`,
      payload,
      {
        responseType: 'text',
      },
    );
  }

  joinCompany(encryptedCompanyId: string): Observable<CompanyMemberType> {
    return this.http.get<CompanyMemberType>(
      `${this.apiEndpoint}/companies/${encryptedCompanyId}/join`,
    );
  }

  removeCompanyMember(companyId: string, memberId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiEndpoint}/companies/${companyId}/members/${memberId}`,
    );
  }

  private extractCompanies(response: CompanyListResponse): CompanyNameType[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.companies)) {
      return response.companies;
    }

    if (Array.isArray(response?.list)) {
      return response.list;
    }

    if (Array.isArray(response?.items)) {
      return response.items;
    }

    return [];
  }

  private sortCompanies<T extends CompanyNameType>(companies: T[]): T[] {
    return [...companies].sort((a, b) =>
      // The backend may return companies without a name.
      (a.name ?? '').localeCompare(b.name ?? '', undefined, {
        sensitivity: 'base',
      }),
    );
  }

  private clearCompaniesCache(): void {
    this.allCompaniesCache.clear();
  }
}
